import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "../../common/reduxQuery/helpers";
import {
  AnnotationSnapshot,
  CounterState,
  DocumentSetData,
} from "../interfaces/documentSet";
import { Status } from "../../common/status/status";
import { User } from "../../user";
import {
  DocumentSetDto,
  HistoryItem,
} from "../../annotationSideControls/interfaces/drawer";

export const documentSetApi = createApi({
  reducerPath: "documentSetApi",
  baseQuery: customBaseQuery(),
  endpoints: (build) => ({
    getDocumentSetCount: build.query<CounterState, string>({
      query: (idString) => ({
        url: `/api/v1/document-sets/count?inputIds=${idString}`,
        method: "GET",
      }),
    }),
    getDocumentSetData: build.query<DocumentSetData, string>({
      query: (documentSetId) => ({
        url: `/api/v1/document-sets/${documentSetId}`,
        method: "GET",
      }),
    }),
    getHistoryOfDocumentSet: build.query<Array<HistoryItem>, string>({
      query: (documentSetId) => ({
        url: `/api/v1/document-sets/history/${documentSetId}`,
        method: "GET",
      }),
    }),
    getDetailsOfDocumentSet: build.query<DocumentSetDto, string>({
      query: (documentSetId) => ({
        url: `/api/v1/document-sets/details/${documentSetId}`,
        method: "GET",
      }),
    }),
    updateDocumentSet: build.query<
      void,
      {
        documentSet: DocumentSetData;
        newStatus?: Status;
        assignee?: User;
        holdReasonId?: string;
      }
    >({
      query: ({ documentSet, newStatus, assignee, holdReasonId }) => {
        let url = `/api/v1/document-sets/${documentSet.id}/update`;

        if (assignee) {
          url = `${url}?assignee=${encodeURI(assignee.id)}`;
        } else {
          url = `${url}?isUnassign=true`;
        }

        if (newStatus) {
          url = `${url}&status=${newStatus}`;
        }

        if (holdReasonId) {
          url = `${url}&holdReasonId=${holdReasonId}`;
        }

        return {
          url: url,
          method: "POST",
        };
      },
    }),
    getAnnotationSnapshot: build.query<
      AnnotationSnapshot,
      { documentSetId: string; version: number }
    >({
      query: ({ documentSetId, version }) => ({
        url: `/api/v1/document-sets/${documentSetId}/annotation-snapshot?version=${version}`,
        method: "GET",
      }),
    }),
    updateEditMode: build.mutation<
      void,
      { documentSetId: string; action: "START" | "CANCEL" | "CONFIRM" }
    >({
      query: ({ documentSetId, action }) => ({
        url: `/api/v1/document-sets/${documentSetId}/edit-mode`,
        method: "POST",
        body: { action },
      }),
    }),
  }),
});

export const {
  useLazyGetDocumentSetCountQuery,
  useLazyGetDocumentSetDataQuery,
  useLazyUpdateDocumentSetQuery,
  useGetHistoryOfDocumentSetQuery,
  useGetDetailsOfDocumentSetQuery,
  useGetAnnotationSnapshotQuery,
  useUpdateEditModeMutation,
} = documentSetApi;
